"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";
import toast from "react-hot-toast";
import { Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";

import { signUpSchema, type SignUpFormData } from "../../../lib/schemas";
import { signUp, checkUsernameAvailability } from "../../../lib/auth";
import { authActions } from "../../../lib/auth/actions";
import {
  loginWithGoogle,
  handleRedirectResult,
} from "../../../lib/auth/snsAuth";
import { Button } from "../../../components/ui/Button";
import { TermsModal } from "../../../components/auth/TermsModal";
import { KOREAN_REGIONS_DETAILED, parseRegion, formatRegion } from "../../../lib/utils/regions";
import {
  validatePassword,
  getPasswordStrength,
} from "../../../lib/utils/passwordValidation";

export default function SignUpPage() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameChecked, setUsernameChecked] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(
    null
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [snsLoading, setSnsLoading] = useState<"google" | null>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsModalType, setTermsModalType] = useState<"service" | "privacy">(
    "service"
  );
  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
  });

  const password = watch("password", "");
  const username = watch("username", "");
  const email = watch("email", "");
  const nickname = watch("nickname", "");
  const region = watch("region", "");
  const agreeTerms = watch("agreeTerms", false);

  // 리다이렉트 로그인 결과 확인
  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        const result = await handleRedirectResult();
        if (result) {
          // 불필요한 메시지 제거
          // toast.success("구글 로그인 성공!");
          router.push("/");
        }
      } catch (error) {
        console.log("리다이렉트 결과 확인:", error);
      }
    };

    checkRedirectResult();
  }, [router]);

  const handleCheckUsername = async () => {
    if (!username || username.length < 4) {
      toast.error("아이디를 4자 이상 입력해주세요.");
      return;
    }

    setIsCheckingUsername(true);
    try {
      const isAvailable = await checkUsernameAvailability(username);
      setUsernameAvailable(isAvailable);
      setUsernameChecked(true);

      if (isAvailable) {
        // 불필요한 메시지 제거
        // toast.success("사용 가능한 아이디입니다!");
      } else {
        toast.error("이미 사용 중인 아이디입니다.");
      }
    } catch (error: any) {
      console.error("아이디 중복확인 오류:", error);
      if (error.message?.includes("활성화되지 않았습니다")) {
        toast.error(error.message);
      } else {
        toast.error("아이디 중복확인 중 오류가 발생했습니다.");
      }
      setUsernameAvailable(null);
      setUsernameChecked(false);
    } finally {
      setIsCheckingUsername(false);
    }
  };

  const onSubmit = async (data: SignUpFormData) => {
    if (!usernameChecked || !usernameAvailable) {
      toast.error("아이디 중복확인을 완료해주세요.");
      return;
    }
    setIsLoading(true);

    try {
      await signUp({
        username: data.username,
        email: data.email,
        password: data.password,
        nickname: data.nickname,
        region: data.region,
        agreeTerms: data.agreeTerms,
      });

      // 불필요한 메시지 제거
      // toast.success("회원가입이 완료되었습니다!");
      router.push("/auth/login");
    } catch (error: any) {
      console.error("❌ 회원가입 실패:", error);
      if (error.code === "auth/operation-not-allowed") {
        toast.error("이메일/비밀번호 인증이 활성화되지 않았습니다. 관리자에게 문의하세요.");
      } else {
        toast.error(
          error instanceof Error
            ? error.message
            : "회원가입 중 오류가 발생했습니다."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // SNS 로그인 핸들러
  const handleGoogleLogin = async () => {
    setSnsLoading("google");
    try {
      const result = await loginWithGoogle();
      if (!result) {
        return;
      }

      // 불필요한 메시지 제거
      // toast.success("구글로 회원가입이 완료되었습니다!");
      router.push("/");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "구글 회원가입 중 오류가 발생했습니다."
      );
    } finally {
      setSnsLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F4FB] py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-[400px]">
        {/* 헤더 */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img
              src="/TM_logo.png"
              alt="톤마켓"
              className="w-auto object-cover"
              style={{ height: '120px' }}
            />
          </div>
          <h1 className="text-[20px] font-semibold text-gray-900 mb-2 tracking-tight">
            ToneMarket에 오신 것을 환영합니다
          </h1>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* 아이디 입력 필드 */}
          <div className="mt-5">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              아이디 <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  autoComplete="username"
                  placeholder="4-20자, 영어와 숫자만 사용 가능"
                  className={`w-full h-12 px-4 border rounded-[12px] transition-all duration-150 focus:outline-none ${
                    errors.username
                      ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/15"
                      : usernameChecked && usernameAvailable
                        ? "border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/15"
                        : usernameChecked && !usernameAvailable
                          ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/15"
                          : "border-[#D7D3F7] focus:border-[#7A5AF8] focus:ring-2 focus:ring-[#7A5AF8]/15"
                  }`}
                  style={{
                    placeholder: "#BDBACF",
                  }}
                  {...register("username")}
                  onChange={e => {
                    register("username").onChange(e);
                    setUsernameChecked(false);
                    setUsernameAvailable(null);
                  }}
                  onKeyDown={e => {
                    if (
                      e.key === "Process" ||
                      /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(e.key)
                    ) {
                      e.preventDefault();
                      setUsernameError(
                        "아이디는 영어와 숫자만 사용할 수 있습니다."
                      );
                      setTimeout(() => setUsernameError(""), 3000);
                    }
                  }}
                  onInput={e => {
                    const target = e.target as HTMLInputElement;
                    const value = target.value.replace(/[^a-zA-Z0-9]/g, "");
                    if (target.value !== value) {
                      target.value = value;
                      register("username").onChange({ target });
                      setUsernameError(
                        "아이디는 영어와 숫자만 사용할 수 있습니다."
                      );
                      setTimeout(() => setUsernameError(""), 3000);
                    }
                  }}
                />
                {usernameChecked && usernameAvailable && (
                  <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
                {usernameChecked && !usernameAvailable && (
                  <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                )}
              </div>
              <button
                type="button"
                onClick={handleCheckUsername}
                disabled={!username || username.length < 4 || isCheckingUsername}
                className="h-12 px-[14px] rounded-[10px] border border-[#D7D3F7] bg-white text-gray-700 font-semibold text-sm hover:border-[#7A5AF8] hover:bg-[#F3F0FF] hover:text-[#7A5AF8] active:bg-[#E5E0FF] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-[#D7D3F7] disabled:hover:bg-white disabled:hover:text-gray-700 whitespace-nowrap"
              >
                {isCheckingUsername ? "확인중..." : "중복확인"}
              </button>
            </div>
            {errors.username && (
              <p className="mt-1.5 text-sm text-red-600">
                {errors.username.message}
              </p>
            )}
            {usernameError && (
              <p className="mt-1.5 text-sm text-red-600">{usernameError}</p>
            )}
            {usernameChecked && usernameAvailable && (
              <p className="mt-1.5 text-sm text-green-600 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                사용 가능한 아이디입니다
              </p>
            )}
            {usernameChecked && !usernameAvailable && (
              <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                <XCircle className="w-4 h-4" />
                이미 사용 중인 아이디입니다
              </p>
            )}
            {!usernameChecked && !usernameError && (
              <p className="mt-1.5 text-sm text-[#9A94C9]">
                영어(대소문자)와 숫자만 사용할 수 있습니다
              </p>
            )}
          </div>

          {/* 비밀번호 입력 필드 */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              비밀번호 <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="8자 이상, 영문+숫자+특수문자 포함"
                className={`w-full h-12 px-4 pr-12 border rounded-[12px] transition-all duration-150 focus:outline-none placeholder:text-[#BDBACF] ${
                  errors.password
                    ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/15"
                    : "border-[#D7D3F7] focus:border-[#7A5AF8] focus:ring-2 focus:ring-[#7A5AF8]/15"
                }`}
                {...register("password")}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9A94C9] hover:text-[#7A5AF8] transition-colors"
                aria-label={showPassword ? "비밀번호 숨기기" : "비밀번호 보기"}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1.5 text-sm text-red-600">
                {errors.password.message}
              </p>
            )}
            {password && (
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        getPasswordStrength(password).score === 0
                          ? "bg-red-500 w-0"
                          : getPasswordStrength(password).score === 1
                            ? "bg-red-500 w-1/4"
                            : getPasswordStrength(password).score === 2
                              ? "bg-orange-500 w-2/4"
                              : getPasswordStrength(password).score === 3
                                ? "bg-yellow-500 w-3/4"
                                : getPasswordStrength(password).score === 4
                                  ? "bg-blue-500 w-full"
                                  : "bg-green-500 w-full"
                      }`}
                    />
                  </div>
                  <span
                    className={`text-xs font-medium ${getPasswordStrength(password).color}`}
                  >
                    {getPasswordStrength(password).label}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* 비밀번호 확인 입력 필드 */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              비밀번호 확인 <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="비밀번호를 다시 입력해주세요"
                className={`w-full h-12 px-4 pr-12 border rounded-[12px] transition-all duration-150 focus:outline-none placeholder:text-[#BDBACF] ${
                  errors.confirmPassword
                    ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/15"
                    : "border-[#D7D3F7] focus:border-[#7A5AF8] focus:ring-2 focus:ring-[#7A5AF8]/15"
                }`}
                {...register("confirmPassword")}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9A94C9] hover:text-[#7A5AF8] transition-colors"
                aria-label={
                  showConfirmPassword ? "비밀번호 숨기기" : "비밀번호 보기"
                }
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1.5 text-sm text-red-600">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* 이메일 입력 필드 */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              이메일 <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              autoComplete="email"
              placeholder="이메일을 입력해주세요"
              className={`w-full h-12 px-4 border rounded-[12px] transition-all duration-150 focus:outline-none placeholder:text-[#BDBACF] ${
                errors.email
                  ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/15"
                  : "border-[#D7D3F7] focus:border-[#7A5AF8] focus:ring-2 focus:ring-[#7A5AF8]/15"
              }`}
              {...register("email")}
            />
            {errors.email && (
              <p className="mt-1.5 text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
            {!errors.email && (
              <p className="mt-1.5 text-sm text-[#9A94C9]">
                비밀번호 찾기 시 사용됩니다
              </p>
            )}
          </div>

          {/* 닉네임 입력 필드 */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              닉네임 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              autoComplete="nickname"
              placeholder="닉네임을 입력해주세요"
              className={`w-full h-12 px-4 border rounded-[12px] transition-all duration-150 focus:outline-none placeholder:text-[#BDBACF] ${
                errors.nickname
                  ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/15"
                  : "border-[#D7D3F7] focus:border-[#7A5AF8] focus:ring-2 focus:ring-[#7A5AF8]/15"
              }`}
              {...register("nickname")}
            />
            {errors.nickname && (
              <p className="mt-1.5 text-sm text-red-600">
                {errors.nickname.message}
              </p>
            )}
            {!errors.nickname && (
              <p className="mt-1.5 text-sm text-[#9A94C9]">
                한글, 영문, 숫자만 사용 가능합니다
              </p>
            )}
          </div>

          {/* 지역 선택 필드 */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              거래 지역 <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3">
              <select
                value={selectedProvince}
                onChange={e => {
                  setSelectedProvince(e.target.value);
                  setSelectedCity("");
                  const selectedRegion = KOREAN_REGIONS_DETAILED.find(r => r.value === e.target.value);
                  // 광역시인 경우 바로 region 설정, 도 단위인 경우 시 선택 필요
                  if (!selectedRegion?.cities || selectedRegion.cities.length === 0) {
                    setValue("region", e.target.value, { shouldValidate: true });
                  } else {
                    setValue("region", "", { shouldValidate: true });
                  }
                }}
                required
                className={`w-full h-12 px-4 border rounded-[12px] transition-all duration-150 focus:outline-none bg-white ${
                  errors.region
                    ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/15"
                    : "border-[#D7D3F7] focus:border-[#7A5AF8] focus:ring-2 focus:ring-[#7A5AF8]/15"
                }`}
              >
                <option value="">시/도를 선택하세요 (필수)</option>
                {KOREAN_REGIONS_DETAILED.map(region => (
                  <option key={region.value} value={region.value}>
                    {region.label}
                  </option>
                ))}
              </select>
              {selectedProvince && (() => {
                const selectedRegion = KOREAN_REGIONS_DETAILED.find(r => r.value === selectedProvince);
                return selectedRegion?.cities && selectedRegion.cities.length > 0 ? (
                  <select
                    value={selectedCity}
                    onChange={e => {
                      setSelectedCity(e.target.value);
                      const regionValue = formatRegion(selectedProvince, e.target.value);
                      setValue("region", regionValue, { shouldValidate: true });
                    }}
                    required
                    className={`w-full h-12 px-4 border rounded-[12px] transition-all duration-150 focus:outline-none bg-white ${
                      errors.region
                        ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/15"
                        : "border-[#D7D3F7] focus:border-[#7A5AF8] focus:ring-2 focus:ring-[#7A5AF8]/15"
                    }`}
                  >
                    <option value="">시를 선택하세요 (필수)</option>
                    {selectedRegion.cities.map(city => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                ) : null;
              })()}
            </div>
            {errors.region && (
              <p className="mt-1.5 text-sm text-red-600">
                {errors.region.message}
              </p>
            )}
          </div>

          {/* 약관 동의 체크박스 */}
          <div className="pt-2">
            <label
              htmlFor="agreeTerms"
              className="flex items-start gap-3 cursor-pointer min-h-[44px] py-2"
            >
              <input
                type="checkbox"
                id="agreeTerms"
                className="mt-0.5 w-5 h-5 rounded border-[#D7D3F7] text-[#7A5AF8] focus:ring-2 focus:ring-[#7A5AF8]/15 focus:ring-offset-0 cursor-pointer"
                {...register("agreeTerms")}
              />
              <span className="text-sm text-gray-700 leading-relaxed flex-1">
                <button
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    setTermsModalType("service");
                    setShowTermsModal(true);
                  }}
                  className="text-[#7A5AF8] hover:text-[#6D46D1] underline"
                >
                  서비스 이용약관
                </button>
                및
                <button
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    setTermsModalType("privacy");
                    setShowTermsModal(true);
                  }}
                  className="text-[#7A5AF8] hover:text-[#6D46D1] underline ml-1"
                >
                  개인정보처리방침
                </button>
                에 동의합니다
              </span>
            </label>
            {errors.agreeTerms && (
              <p className="mt-1.5 text-sm text-red-600 ml-8">
                {errors.agreeTerms.message}
              </p>
            )}
          </div>

          {/* 회원가입 버튼 */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-[52px] rounded-[14px] font-bold text-base text-white transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
              style={{
                background: "linear-gradient(135deg, #7A5AF8, #9B82FF)",
                boxShadow: "0 4px 12px rgba(122, 90, 248, 0.22)",
              }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              ) : (
                "회원가입"
              )}
            </button>
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full h-px bg-[#E0DDF5]" />
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-[#F5F4FB] text-[12px] text-[#9A94C9]">
                또는
              </span>
            </div>
          </div>

          {/* SNS 로그인 버튼들 */}
          <div className="space-y-3">
            {/* 구글 로그인 */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={snsLoading === "google"}
              className="w-full h-12 bg-white border border-[#D7D3F7] text-gray-700 font-medium rounded-[12px] hover:bg-gray-50 active:scale-[0.98] transition-all duration-150 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {snsLoading === "google" ? (
                <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span>구글로 계속하기</span>
                </>
              )}
            </button>
          </div>

          {/* 로그인 링크 */}
          <div className="text-center pt-4">
            <p className="text-sm text-gray-600">
              이미 계정이 있으신가요?{" "}
              <Link
                href="/auth/login"
                className="font-semibold text-[#7A5AF8] hover:text-[#6D46D1] transition-colors"
              >
                로그인하기
              </Link>
            </p>
          </div>
        </form>
      </div>

      {/* 약관 모달 */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        type={termsModalType}
      />

      {/* reCAPTCHA 컨테이너 */}
      <div id="recaptcha-container"></div>
    </div>
  );
}
